/** Imports */
import {z} from 'zod';
import {zMongoObject, zMongoObjectId} from '@/types/utils';
import {zMediaWithThumbnail} from '@/mongoSchemas/Post/Medias.zod';
import {zOrganization} from '@/mongoSchemas/Organization/Organizations.zod';
import {type Platform, zPage, zPlatform} from '@/mongoSchemas/Page/Pages.zod';
import {zTikTokCreatorInfo, type TikTokCreatorInfo} from '@/mongoSchemas/TikTokCreatorInfo.zod';


/** Types */
export type YouTubeVisibility = 'public' | 'unlisted' | 'private';
export type TikTokVisibility = 'PUBLIC_TO_EVERYONE' | 'FOLLOWER_OF_CREATOR' | 'MUTUAL_FOLLOW_FRIENDS' | 'SELF_ONLY';
export type ThreadsVisibility = z.infer<typeof zThreadsVisibility>;
export type PageSettings = z.infer<typeof zPostSettings>;
export type Sentiment = 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
export type Transformations = {
    rotation: number;
    isFlippedX: boolean;
    isFlippedY: boolean;
};
export type Filters = {
    disabled: boolean;
    brightness: number;
    contrast: number;
    saturation: number;
    hue: number;
};
export type SlicerSettings = {
    numberOfSlices: number;
    infillPercentage: number;
    makeSquare: boolean;
    backgroundFillType: BackgroundFillType;
    backgroundFillColor: string;
    backgroundFillBlurRadius: number;
};
type BackgroundFillType = 'blurred' | 'solid' | 'checkerboard';
export type PostState = 'draft' | 'pending' | 'inPosting' | 'posted' | 'failed' | 'deleted';
export type PostLogSeverity = 'info' | 'warning' | 'error';
export type StateChange = {
    timestamp: Date;
    state: PostState;
    operationKey?: string;
};
export type PostLog = {
    severity: PostLogSeverity;
    message: string;
    timestamp: Date;
    additionalData?: any;
};
export type {TikTokCreatorInfo};
export type YouTubeCategory = {
    id: string;
    title: string;
};
export type PostSettings = z.infer<typeof zPostSettings>;
export type FacebookPostSettings = z.infer<typeof zPostSettingsFacebook>;
export type InstagramPostSettings = z.infer<typeof zPostSettingsInstagram>;
export type TikTokPostSettings = z.infer<typeof zPostSettingsTikTok>;
export type YouTubePostSettings = z.infer<typeof zPostSettingsYouTube>;
export type ThreadsPostSettings = z.infer<typeof zPostSettingsThreads>;
export type TwitterPostSettings = z.infer<typeof zPostSettingsTwitter>;
export type FacebookPostInsights = z.infer<typeof zFacebookPostInsights>;
export type InstagramPostInsights = z.infer<typeof zInstagramPostInsights>;
export type TikTokPostInsights = z.infer<typeof zTikTokPostInsights>;
export type YouTubePostInsights = z.infer<typeof zYoutubePostInsights>;
export type ThreadsPostInsights = z.infer<typeof zThreadsPostInsights>;
export type TwitterPostInsights = z.infer<typeof zTwitterPostInsights>;
export type Post = z.infer<typeof zPost>;
export type ReducedPost = {
    _id: string;
    platformMediaId: string | undefined;
    pageId: string;
    state: PostState;
    isUnderReview?: boolean;
    schedule: Date;
    socialMediaPlatform: Platform;
    source: 'mediafox' | 'external';
    articleId?: string;
    articleUrl?: string;
    rssItemId?: number;
    evergreenId?: number;
    crosspostCandidateId?: number;
    bulkTaskId?: number;
    bulkTaskEntryId?: number;
    isBulkGenerated?: boolean;
    postCategoryId?: number | null;
    postTags?: Array<string>;
    isRepost?: boolean;
    repostOfPostId?: string;
    repostOfPlatformMediaId?: string;
    repostOfSource?: 'mediafox' | 'external';
};
export type ReducedCalendarPost = Omit<ReducedPost, 'schedule'> & {
    // Same as reduced post, but schedule is a number (timestamp) for less data transfer and faster parsing on the client
    schedule: number;
}
export {zTikTokCreatorInfo} from '@/mongoSchemas/TikTokCreatorInfo.zod';


/** Zod schemas */
export const zFacebookInstagramPostingType = z.enum([
    'feed',
    'story',
    'reel',
]);
export const zTikTokVisibility = z.enum([
    'PUBLIC_TO_EVERYONE',
    'FOLLOWER_OF_CREATOR',
    'MUTUAL_FOLLOW_FRIENDS',
    'SELF_ONLY',
]);
export const zYouTubeVisibility = z.enum([
    'public',
    'unlisted',
    'private',
]);
export const zThreadsVisibility = z.enum([
    'everyone',
    'accounts_you_follow',
    'mentioned_only',
    'parent_post_author_only',
    'followers_only',
]);
export const zThreadsReplyControl = z.enum([
    'everyone',
    'accounts_you_follow',
    'mentioned_only',
    'parent_post_author_only',
    'followers_only',
]);
export const zPostSettingsBase = z.object({
    useOverlay: z.boolean().optional(),

    // RSS settings
    shortCode: z.string().nullish(),
    shortLink: z.string().nullish(),
    shortLinkTitle: z.string().nullish(),
    hasBeenRepostedByRss: z.boolean().optional(),
    rssLinkOnlyPost: z.boolean().optional(),
    rssPostingBucket: z.number().int().min(1).max(5).optional(),
    hideLink: z.boolean().optional(),

    // Crossposting
    eligibleForCrosspost: z.boolean().optional(),
});
export const zPostSettingsFacebook = zPostSettingsBase.extend(z.object({
    postingType: z.enum(['feed', 'story', 'reel']),
    fbDeterminedPostType: z.enum(['pagepost', 'video']).optional(),
    fbDeterminedInsightsId: z.string().optional(),
}).shape);
export const zPostSettingsInstagram = zPostSettingsBase.extend(z.object({
    allowComments: z.boolean(),
    postingType: z.enum(['feed', 'story', 'reel']),
}).shape);
export const zTikTokAnchorLink = z.string()
    .trim()
    .max(2_048)
    .pipe(z.url())
    .refine((url) => new URL(url).protocol === 'https:', {
        message: 'TIKTOK_ANCHOR_LINK_HTTPS_REQUIRED',
    });
export const zPostSettingsTikTok = zPostSettingsBase.extend({
    tiktokAnchorLink: zTikTokAnchorLink.optional(),
    tiktokAnchorCta: z.string().trim().min(1).max(35).optional(),
    tiktokAnchorResolvedLink: zTikTokAnchorLink.optional(),
    tiktokAnchorResolvedCta: z.string().trim().min(1).max(35).optional(),
    tiktokAnchorResolvedAt: z.instanceof(Date).optional(),
    allowComments: z.boolean().optional(),
    allowDuet: z.boolean().optional(),
    allowStitch: z.boolean().optional(),
    postAsDraft: z.boolean().optional(),
    visibility: zTikTokVisibility.optional(),
    autoAddMusic: z.boolean().optional(),
    discloseContent: z.boolean().optional(),
    discloseContent_brandOrganic: z.boolean().optional(),
    discloseContent_brandContent: z.boolean().optional(),
    tiktokCreatorInfo: zTikTokCreatorInfo,
}).superRefine((settings, ctx) => {
    const hasCta = Boolean(settings.tiktokAnchorCta);
    if (!hasCta || settings.tiktokAnchorLink) return;
    ctx.addIssue({
        code: 'custom',
        message: 'TIKTOK_ANCHOR_LINK_REQUIRED',
        path: ['tiktokAnchorLink'],
    });
});
export const zPostSettingsYouTube = zPostSettingsBase.extend(z.object({
    madeForKids: z.boolean(),
    categoryId: z.string(),
    visibility: zYouTubeVisibility,
}).shape);
export const zPostSettingsThreads = zPostSettingsBase.extend(z.object({
    visibility: zThreadsVisibility,
    threadsReplyControl: zThreadsReplyControl,
}).shape);
export const zPostSettingsTwitter = zPostSettingsBase.extend(z.object({}).shape);
export const zPostSettings = z.union([
    zPostSettingsFacebook,
    zPostSettingsInstagram,
    zPostSettingsTikTok,
    zPostSettingsYouTube,
    zPostSettingsThreads,
    zPostSettingsTwitter,
]);
export const zFacebookPostInsights = z.object({
    reach: z.number(),
    impressions: z.number(),
    likeCount: z.number(),
    commentCount: z.number(),
    shareCount: z.number(),
    interactions: z.number(),
    engagementRate: z.number(),
    playCount: z.number().optional(),
    averageWatchTime: z.number().optional(),
    totalWatchTime: z.number().optional(),
    retentionGraph: z.record(z.string(), z.number()).optional(),
    monetizationApproximateEarnings: z.number().optional(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
    viewsByFollowerType: z.object({
        followers: z.number().int().nonnegative(),
        nonFollowers: z.number().int().nonnegative(),
        unknown: z.number().int().nonnegative().optional(),
    }).optional(),
});
export const zInstagramPostInsights = z.object({
    reach: z.number(),
    impressions: z.number(),
    likeCount: z.number(),
    commentCount: z.number(),
    shareCount: z.number(),
    savedCount: z.number(),
    profileVisits: z.number(),
    interactions: z.number(),
    engagementRate: z.number(),
    totalWatchTime: z.number(),
    averageWatchTime: z.number(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
});
export const zTikTokPostInsights = z.object({
    reach: z.number(),
    likeCount: z.number(),
    commentCount: z.number(),
    shareCount: z.number(),
    interactions: z.number(),
    engagementRate: z.number(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
});
export const zYoutubePostInsights = z.object({
    likeCount: z.number(),
    dislikeCount: z.number(),
    commentCount: z.number(),
    viewCount: z.number(),
    shareCount: z.number(),
    averageViewDuration: z.number(),
    audienceRetention: z.tuple([z.number(), z.number()]).array(),
    totalInteractions: z.number(),
    engagementRate: z.number(),
    subscribersGained: z.number(),
    subscribersLost: z.number(),
    monetizationApproximateEarnings: z.number().optional(),
    adImpressions: z.number().nonnegative().optional(),
    cpm: z.number().nonnegative().optional(),
    thumbnailImpressions: z.number().nonnegative().optional(),
    thumbnailImpressionsCtr: z.number().nonnegative().optional(),
    engagedViews: z.number().nonnegative().optional(),
    watchTimeMinutes: z.number().nonnegative().optional(),
    averagePercentageViewed: z.number().nonnegative().optional(),
    trafficSources: z.array(z.object({
        source: z.string(),
        engagedViews: z.number().nonnegative(),
        views: z.number().nonnegative(),
        watchTimeMinutes: z.number().nonnegative(),
    })).optional(),
    endScreenImpressions: z.number().nonnegative().optional(),
    endScreenClicks: z.number().nonnegative().optional(),
    endScreenClickRate: z.number().nonnegative().optional(),
    subscriberConversionRate: z.number().optional(),
    revenuePerThousandViews: z.number().optional(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
    viewsByFollowerType: z.object({
        followers: z.number().int().nonnegative(),
        nonFollowers: z.number().int().nonnegative(),
        unknown: z.number().int().nonnegative().optional(),
    }).optional(),
});
export const zThreadsPostInsights = z.object({
    views: z.number(),
    likes: z.number(),
    replies: z.number(),
    reposts: z.number(),
    quotes: z.number(),
    shares: z.number(),
    engagementRate: z.number(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
});
export const zTwitterPostInsights = z.object({
    likes: z.number(),
    replies: z.number(),
    reposts: z.number(),
    quotes: z.number(),
    bookmarks: z.number(),
    impressions: z.number(),
    engagementRate: z.number(),
    clicks: z.number().optional(),
    clickRate: z.number().optional(),
});
export const zPostInsights = z.union([
    zFacebookPostInsights,
    zInstagramPostInsights,
    zTikTokPostInsights,
    zYoutubePostInsights,
    zThreadsPostInsights,
    zTwitterPostInsights,
]);
export const zPostingState = z.enum([
    'draft',
    'pending',
    'inPosting',
    'posted',
    'failed',
    'deleted',
]);
export const zPostLogSeverity = z.enum([
    'info',
    'warning',
    'error',
]);
export const zStateChange = z.object({
    timestamp: z.union([
        z.string(),
        z.instanceof(Date),
    ]),
    state: zPostingState,
    operationKey: z.string().min(1).optional(),
});
export const zPost = zMongoObject.extend({

    // Keys
    pageId: z.union([
        zMongoObjectId,
        zPage,
    ]),
    organizationId: z.union([
        zMongoObjectId,
        zOrganization,
    ]),
    platformMediaId: z.string().optional(),
    platformEditLease: z.object({
        token: z.string(),
        expiresAt: z.date(),
    }).optional(),
    socialMediaPlatform: zPlatform,
    permalink: z.string(),
    retries: z.number().int().optional(),
    articleId: z.string().min(1).optional(),
    articleUrl: z.url().optional(),
    rssItemId: z.number().int().positive().optional(),
    evergreenId: z.number().int().positive().optional(),
    crosspostCandidateId: z.number().int().positive().optional(),
    bulkTaskId: z.number().int().positive().optional(),
    bulkTaskEntryId: z.number().int().positive().optional(),
    isBulkGenerated: z.boolean().optional(),
    isRepost: z.boolean().optional(),
    repostOfPostId: zMongoObjectId.optional(),
    repostOfPlatformMediaId: z.string().min(1).optional(),
    repostOfSource: z.enum(['mediafox', 'external']).optional(),

    // Data
    caption: z.string().optional(),
    description: z.string().optional(),
    firstComment: z.string().optional(),
    firstCommentId: z.string().min(1).optional(),
    hashTags: z.string().array(),
    postCategoryId: z.number().int().nullable().optional(),
    postTags: z.string().array().optional().default([]),
    postTaxonomyRepairToken: z.string().optional(),
    postTaxonomyRepairRequestedAt: z.date().optional(),
    mediaList: zMediaWithThumbnail.array(),
    settings: zPostSettings,
    schedule: z.union([
        z.string(),
        z.instanceof(Date),
    ]),
    insights: zPostInsights.optional(),
    isUnderReview: z.boolean().optional().default(false),

    // Debug
    state: zPostingState,
    stateChanges: zStateChange.array(),
    errorMessage: z.string().optional(),
    logs: z.object({
        // SQL history ID is absent on legacy Mongo snapshots and newly constructed events.
        id: z.number().int().positive().optional(),
        severity: zPostLogSeverity,
        message: z.string(),
        timestamp: z.union([
            z.string(),
            z.instanceof(Date),
        ]),
        additionalData: z.any().optional(),
    }).array(),
    traceParentId: z.string().optional(),
    lastInsightsUpdate: z.instanceof(Date).optional(),
    lastTimeCommentsFetched: z.instanceof(Date).optional(),
});
export const zPageSchedule = z.object({
    pageId: z.string(),
    schedule: z.string(),
});
